<template>
  <div class="card listing-card">
    <form @submit.prevent class="needs-validation" novalidate>
      <div v-if="serverError" class="alert alert-danger" role="alert">
        {{serverError}}
      </div>
      <div class="card-body">
        <div class="label-text">
          <span v-if="!builtEnvironmentFeature.id">Add Built Environment Feature</span>
          <span v-if="builtEnvironmentFeature.id">Edit Built Environment Feature</span>
        </div>
        <hr/>
        <template v-if="builtEnvironmentFeature.id">
          <div class="form-group row">
            <label for="description" class="col-lg-4 col-form-label label-text">
              Feature ID
            </label>
            <div class="col-lg-8">
              {{builtEnvironmentFeature.id}}
            </div>
          </div>
        </template>
        <div class="form-group row">
          <label for="description" class="col-lg-4 col-form-label label-text">
            <span class="text-danger">*</span>Description
          </label>
          <div class="col-lg-8">
            <input data-vv-as="Description"
                   type="text"
                   class="form-control"
                   v-validate="'required'"
                   name="description"
                   v-model="builtEnvironmentFeature.description"
                   placeholder="Enter Description"
                   maxlength="50" />
            <small v-if="!errors.first('description')" class="form-text text-muted">
              Max 50 characters
            </small>
            <div class="invalid-feedback">{{ errors.first('description') }}</div>
          </div>
        </div>
        <div class="form-group row">
          <label for="isBuilding" class="col-lg-4 label-text">
            Is Building?
          </label>
          <div class="col-lg-8">
            <b-form-checkbox
              v-model="builtEnvironmentFeature.isBuilding"
              name="isBuilding"
            >
            </b-form-checkbox>
          </div>
        </div>
        <div class="form-group row">
          <label for="existing" class="col-lg-4 label-text">
            Existing?
          </label>
          <div class="col-lg-8">
            <b-form-checkbox
              v-model="builtEnvironmentFeature.exists"
              name="exists"
            >
            </b-form-checkbox>
          </div>
        </div>
        <div class="form-group row">
          <label for="dwellingUnits" class="col-lg-4 label-text">
            <span class="text-danger">*</span>Dwelling Units
          </label>
          <div class="col-lg-8">
            <input data-vv-as="Dwelling Units"
              ref="dwellingUnits"
              v-validate="'required|numeric'"
              class="form-control"
              name="dwellingUnits"
              v-model="builtEnvironmentFeature.dwellingUnits"
              placeholder="Enter number of Dwelling Units"
              maxlength="18"
            />
            <small v-if="!errors.first('dwellingUnits')" class="form-text text-muted">
              Must be a positive whole number or 0
            </small>
            <div class="invalid-feedback">{{ errors.first('dwellingUnits') }}</div>
          </div>
        </div>
        <div class="form-group row">
          <label for="finishedArea" class="col-lg-4 label-text">
            <span class="text-danger">*</span>Finished Area Square Feet
          </label>
          <div class="col-lg-8">
            <input data-vv-as="Finished Area"
              v-validate="'required|numeric|min_value_if:dwellingUnits'"
              class="form-control"
              name="finishedArea"
              v-model="builtEnvironmentFeature.finishedArea"
              placeholder="Enter Finished Area Square Feet"
              maxlength="18"
            />
            <small v-if="!errors.first('finishedArea')" class="form-text text-muted">
              Must be a positive whole number or 0
            </small>
            <div class="invalid-feedback" style="display:block">
              {{ errors.first('finishedArea') }}
            </div>
          </div>
        </div>
        <div class="form-group row">
          <label for="imperviousArea" class="col-lg-4 label-text">
            <span class="text-danger">*</span>Impervious Area Square Feet
          </label>
          <div class="col-lg-8">
            <input data-vv-as="Impervious Area"
              v-validate="'required|numeric'"
              v-model="builtEnvironmentFeature.imperviousArea"
              class="form-control"
              name="imperviousArea"
              placeholder="Enter Impervious Area Square Feet"
              maxlength="18"
            />
            <small v-if="!errors.first('imperviousArea')" class="form-text text-muted">
              Must be a positive whole number or 0
            </small>
            <div class="invalid-feedback" style="display:block">
              {{ errors.first('imperviousArea') }}
            </div>
          </div>
        </div>
        <div class="form-group row">
          <label for="primarySource" class="col-lg-4 label-text">
            Primary Source
          </label>
          <div class="loading col-lg-5" v-if="primarySourcesLoading">
            Loading...
          </div>
          <div class="col-lg-8">
            <select v-if="!primarySourcesLoading"
                    data-vv-as="Primary Source"
                    class="form-control"
                    name="primarySource"
                    v-model="builtEnvironmentFeature.primarySourceId">
              <option :value="null" disabled>Select Primary Source</option>
              <option v-for="option in primarySourceOptions"
                      :key="option.id"
                      :value="option.id">
              {{ option.name }}
              </option>
            </select>
            <div class="invalid-feedback">{{ errors.first('primarySource') }}</div>
          </div>
        </div>
        <div class="form-group row">
          <label for="notes" class="col-lg-4 col-form-label label-text">
            Feature Notes
          </label>
          <div class="col-lg-8">
            <textarea data-vv-as="Notes"
                      type="textarea"
                      class="form-control"
                      name="notes"
                      v-model="builtEnvironmentFeature.notes"
                      placeholder="Enter Notes"
                      rows="2"
                      maxlength="100" />
              <small v-if="!errors.first('notes')" class="form-text text-muted">
                Max 100 characters
              </small>
          </div>
        </div>
      </div>
      <div class='card-footer right-align-content'>
        <b-button :disabled="submitting" variant="danger" class="button-row-spacing"
                  v-b-modal="'cancelBuiltEnvironment-' + builtEnvironmentFeature.id">
          Cancel
        </b-button>
        <b-button :disabled="submitting" @click="submitWrapper(builtEnvironmentFeature)">
          Save
        </b-button>
      </div>
    </form>
    <CancelModal
      :modalId="'cancelBuiltEnvironment-' + builtEnvironmentFeature.id"
      @cancelled="cancelEdit">
    </CancelModal>
  </div>
</template>
<script>
import { mapGetters, mapState } from 'vuex';
import FormSubmit from '../../mixins/FormSubmit';
import CancelModal from '../CancelModal.vue';
import { EntityModules, LookupModules } from '@/store/moduleConfig';

export default {
  name: 'BuiltEnvironmentForm',
  components: {
    CancelModal,
  },
  mixins: [FormSubmit],
  props: [
    'existingEntity',
    'submitCallback',
  ],
  data() {
    return {
      validationAttempted: false,
      builtEnvironmentFeature: {
        id: this.existingEntity.id,
        propertyId: this.existingEntity.propertyId,
        description: this.existingEntity.description,
        isBuilding: this.existingEntity.isBuilding,
        exists: this.existingEntity.exists,
        dwellingUnits: this.existingEntity.dwellingUnits,
        imperviousArea: this.existingEntity.imperviousArea,
        finishedArea: this.existingEntity.finishedArea,
        primarySourceId: this.existingEntity.primarySourceId,
        notes: this.existingEntity.notes,
        updatedAt: this.existingEntity.updatedAt,
      },
    };
  },
  computed: {
    ...mapState(LookupModules.PRIMARY_SOURCES.namespace, {
      primarySourcesLoading: state => state.loading,
    }),
    ...mapState(EntityModules.BUILT_ENVIRONMENT_FEATURE.namespace, {
      serverError: state => state.serverError,
      submitting: state => state.submitting,
    }),
    ...mapGetters(LookupModules.PRIMARY_SOURCES.namespace, {
      primarySourceOptions: 'getActiveOptions',
    }),
  },
  methods: {
    cancelEdit() {
      this.$emit('closeForm', this.builtEnvironmentFeature.id);
    },
    submitWrapper(entity) {
      this.onSubmit(
        entity,
        EntityModules.BUILT_ENVIRONMENT_FEATURE.namespace,
        [],
        this.submitCallback,
      );
      this.validationAttempted = true;
    },
  },
};
</script>
